import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section } from '@dized/ui';
import { WorldHeatmapView } from './WorldHeatmapView';
import { getMarketConcentrationLevel } from '../../common/config/analyticsConfig';

const GeographicalInsights = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const InsightRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InsightLabel = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const InsightValue = styled.span`
  font-weight: 600;
  color: #495057;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#2196F3' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#1976D2' : '#f5f5f5'};
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  position: relative;
`;

const MapPlaceholder = styled.div`
  text-align: center;
  color: #666;
  font-size: 1rem;
`;

const CountryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
`;

const CountryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const CountryName = styled.span`
  font-weight: 500;
  color: #333;
`;

const CountryStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const CountryCount = styled.span`
  font-size: 1.1rem;
  font-weight: bold;
  color: #2196F3;
`;

const CountryPercentage = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

export const UserDistribution = ({ data }) => {
  const [viewMode, setViewMode] = useState('map');
  
  if (!data || !data.country_distribution || data.country_distribution.length === 0) {
    return (
      <Section>
        <h3>User Distribution</h3>
        <p>No geographic distribution data available</p>
      </Section>
    );
  }

  const countries = data.country_distribution;
  const maxCount = Math.max(...countries.map(c => c.count));
  
  // Calculate geographical insights
  const totalCountries = countries.length;
  const topCountry = countries.length > 0 ? countries[0] : { country: 'N/A', percentage: 0 };
  const topThreeCountries = countries.slice(0, 3);
  const topThreePercentage = topThreeCountries.reduce((sum, c) => sum + c.percentage, 0);
  const topThreeLabel = topThreeCountries.map(c => c.country).join(', ');

  return (
    <Section>
      <h3>User Distribution by Country</h3>
      
      <GeographicalInsights>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: '#495057' }}>📊 Geographic Insights</h4>
        <InsightRow>
          <InsightLabel>Total Countries:</InsightLabel>
          <InsightValue>{totalCountries}</InsightValue>
        </InsightRow>
        <InsightRow>
          <InsightLabel>Top Market:</InsightLabel>
          <InsightValue>{topCountry.country} ({topCountry.percentage.toFixed(1)}%)</InsightValue>
        </InsightRow>
        <InsightRow>
          <InsightLabel>Top 3 Markets:</InsightLabel>
          <InsightValue>{topThreeLabel} ({topThreePercentage.toFixed(1)}%)</InsightValue>
        </InsightRow>
        <InsightRow>
          <InsightLabel>Market Concentration:</InsightLabel>
          <InsightValue>{getMarketConcentrationLevel(topThreePercentage)}</InsightValue>
        </InsightRow>
      </GeographicalInsights>
      
      <ViewToggle>
        <ToggleButton 
          active={viewMode === 'map'} 
          onClick={() => setViewMode('map')}
        >
          🗺️ Heatmap View
        </ToggleButton>
        <ToggleButton 
          active={viewMode === 'list'} 
          onClick={() => setViewMode('list')}
        >
          📊 List View
        </ToggleButton>
      </ViewToggle>

      {viewMode === 'map' ? (
        <WorldHeatmapView countryData={countries} />
      ) : (
        <MapContainer>
          <MapPlaceholder>
            � Country Statistics List View
            <br />
            <small>(Use toggle above to switch to Heatmap View)</small>
          </MapPlaceholder>
        </MapContainer>
      )}

      <CountryList>
        {countries.slice(0, 10).map((country, index) => (
          <CountryItem key={index}>
            <div style={{ flex: 1 }}>
              <CountryName>{country.country}</CountryName>
              <ProgressBar>
                <ProgressFill percentage={(country.count / maxCount) * 100} />
              </ProgressBar>
            </div>
            <CountryStats>
              <CountryCount>{country.count}</CountryCount>
              <CountryPercentage>{country.percentage}%</CountryPercentage>
            </CountryStats>
          </CountryItem>
        ))}
      </CountryList>
    </Section>
  );
};

UserDistribution.propTypes = {
  data: PropTypes.shape({
    country_distribution: PropTypes.arrayOf(
      PropTypes.shape({
        country: PropTypes.string,
        count: PropTypes.number,
        percentage: PropTypes.number
      })
    )
  })
};

UserDistribution.displayName = 'UserDistribution';
